import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Monitor, Smartphone, LogOut, RefreshCw } from 'lucide-react';
import api from '../api';

const moment = value => value ? new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : 'Нет данных';

export default function ActiveSessions({ accountId }) {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pending, setPending] = useState(null);
    const [notice, setNotice] = useState('');

    const load = () => {
        setLoading(true);
        setError('');
        return api.get('/api/device-sessions').then(({ data }) => setSessions(data))
            .catch(() => setError('Не удалось загрузить сеансы. Попробуйте ещё раз.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setNotice('');
        api.get('/api/device-sessions').then(({ data }) => { if (active) setSessions(data); })
            .catch(() => { if (active) setError('Не удалось загрузить сеансы. Попробуйте ещё раз.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [accountId]);

    const end = async session => {
        if (pending) return;
        const all = session === null;
        if (all && !window.confirm('Завершить все сеансы этого аккаунта, включая текущее устройство?')) return;
        setPending(all ? 'all' : session.id);
        setError('');
        setNotice('');
        try {
            const { data } = await api.delete(all ? '/api/device-sessions' : `/api/device-sessions/${session.id}`, {
                params: { accountId },
            });
            if (data.currentDeviceEnded) {
                navigate('/login', { replace: true });
                return;
            }
            setSessions(previous => all ? [] : previous.filter(item => item.id !== session.id));
            setNotice(all ? 'Все сеансы завершены.' : 'Сеанс завершён.');
        } catch (err) {
            setError(err.response?.status === 409
                ? 'Аккаунт изменился в другой вкладке. Обновите страницу перед завершением сеансов.'
                : 'Не удалось завершить сеанс. Попробуйте ещё раз.');
        } finally {
            setPending(null);
        }
    };

    return (
        <section className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200" aria-labelledby="active-sessions-title">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 id="active-sessions-title" className="m-0 text-lg font-semibold text-gray-900">Активные сеансы</h3>
                <button type="button" onClick={() => end(null)} disabled={loading || !!pending || sessions.length === 0}
                    className="text-red-500 px-3.5 py-2 border border-red-300 rounded-lg text-sm font-medium transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    {pending === 'all' ? 'Завершаем…' : 'Завершить все сеансы'}
                </button>
            </div>
            <p className="text-gray-500 text-sm mt-0 mb-4">
                Устройства, на которых выполнен вход в этот аккаунт. Завершение сеанса отключает аккаунт
                на выбранном устройстве и отзывает связанные токены входа в продукты.
            </p>
            {error && <div role="alert" className="flex items-start gap-2 text-sm text-red-600 mb-4">
                <AlertTriangle size={18} className="shrink-0" /><span>{error}</span>
            </div>}
            {notice && <p role="status" className="text-sm text-emerald-600">{notice}</p>}
            {loading ? <p role="status" className="text-sm text-gray-500">Загружаем сеансы…</p> : (
                <>
                    {!error && sessions.length === 0 && <p className="text-sm text-gray-500">Активных сеансов нет.</p>}
                    <ul className="list-none p-0 m-0">
                        {sessions.map(session => {
                            const mobile = /android|ios|iphone|ipad/i.test(`${session.platform || ''} ${session.deviceName || ''}`);
                            return <li key={session.id} className="flex flex-wrap sm:flex-nowrap items-start gap-3 py-4 border-b border-gray-100 last:border-b-0">
                                <div className="p-2 rounded-lg bg-gray-100 text-gray-600 shrink-0">
                                    {mobile ? <Smartphone size={20} /> : <Monitor size={20} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="font-medium text-gray-900 break-words">{session.deviceName || 'Неизвестное устройство'}</span>
                                        {session.current && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Это устройство</span>}
                                    </div>
                                    <p className="text-sm text-gray-500 m-0 break-words">
                                        {session.location || 'Локация не определена'}{session.ip && ` · ${session.ip}`}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1 mb-0">Последняя активность: {moment(session.lastAccessedAt)}</p>
                                    <p className="text-xs text-gray-500 mt-1 mb-0">Вход: {moment(session.createdAt)}
                                        {session.appVersion && ` · Версия приложения: ${session.appVersion}`}
                                    </p>
                                </div>
                                <button type="button" disabled={!!pending} onClick={() => end(session)}
                                    aria-label={`Завершить сеанс: ${session.deviceName || 'Неизвестное устройство'}`}
                                    className="flex items-center gap-2 text-red-500 px-3.5 py-2 border border-red-300 rounded-lg text-sm font-medium transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                                    <LogOut size={16} />{pending === session.id ? 'Завершаем…' : 'Завершить'}
                                </button>
                            </li>;
                        })}
                    </ul>
                    {error && sessions.length === 0 && <button type="button" onClick={load}
                        className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
                        <RefreshCw size={16} />Повторить загрузку
                    </button>}
                </>
            )}
        </section>
    );
}
